import type { ReactElement } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ExternalLink } from 'lucide-react';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import type { ShadowAiToolUser } from '@kirin/api-client/contracts/shadow-ai';
import { cn } from '@/lib/utils';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { LoggedInUserText } from './LoggedInUserText';
import { EndpointDetailsPopover } from '@/components/Inventory/EndpointDetailsPopover';

export function ToolUsersTable({
  users,
  onUserClick,
  highlightUserId = null,
  highlightedRowRef,
  showLoggedInUser = false,
}: {
  users: ShadowAiToolUser[];
  /**
   * Drill into a user. Omit on a surface that is already scoped to one user
   * (the Shadow AI user-scope page) — rows then render non-interactive instead
   * of looking clickable and doing nothing (KIR-3495).
   */
  onUserClick?: (
    userId: string,
    userInfo?: { email?: string; name?: string }
  ) => void;
  highlightUserId?: string | null;
  highlightedRowRef?: React.RefObject<HTMLTableRowElement | null>;
  /** Show the "Logged-in User" column (KIR-2916). Gated by feature flag upstream. */
  showLoggedInUser?: boolean;
}): ReactElement {
  // KIR-3035: the "Machine"→"Endpoint" rename + endpoint-details cell are
  // flag-gated. When off, render the legacy "Machine" header + raw machine id.
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const endpointEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  return (
    <>
      <div className="text-sm font-medium text-muted-foreground mb-2">
        Users ({users.length})
      </div>
      {users.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading users...</div>
      ) : (
        <div className="rounded-md border border-table-border overflow-hidden bg-background">
          <Table className="text-xs">
            <TableHeader className="sticky top-0 z-20 border-b bg-muted">
              <TableRow className="!bg-transparent hover:!bg-transparent">
                <TableHead>User</TableHead>
                <TableHead>
                  {endpointEnabled ? 'Endpoint' : 'Machine'}
                </TableHead>
                {showLoggedInUser && <TableHead>Logged-in User</TableHead>}
                <TableHead>Last Seen</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user, idx) => {
                const isHighlighted = highlightUserId === user.userId;
                return (
                  <TableRow
                    key={`${user.userId}-${user.machineId}-${idx}`}
                    ref={isHighlighted ? highlightedRowRef : undefined}
                    className={cn(
                      'border-b',
                      onUserClick && 'cursor-pointer hover:bg-muted/50',
                      isHighlighted &&
                        'bg-blue-50 dark:bg-blue-950/30 animate-pulse'
                    )}
                    onClick={
                      onUserClick &&
                      (e => {
                        e.stopPropagation();
                        onUserClick(user.userId, {
                          email: user.email ?? undefined,
                          name: user.name ?? undefined,
                        });
                      })
                    }
                  >
                    <TableCell className="text-sm h-12 px-3 py-2">
                      {user.email ?? user.name ?? user.userId}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground h-12 px-3 py-2">
                      {endpointEnabled ? (
                        <EndpointDetailsPopover
                          userId={user.userId}
                          machineId={user.machineId}
                          endpointName={user.endpointName}
                        />
                      ) : (
                        <span className="font-mono">{user.machineId}</span>
                      )}
                    </TableCell>
                    {showLoggedInUser && (
                      <TableCell className="text-sm h-12 px-3 py-2">
                        <LoggedInUserText
                          // `||` (not `??`): an empty-string email should fall
                          // through to the accountId (e.g. GitHub username).
                          email={
                            user.loggedInUser?.accountEmail ||
                            user.loggedInUser?.accountId
                          }
                          match={user.loggedInUserMatch}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-muted-foreground h-12 px-3 py-2">
                      <RelativeTimeWithTooltip date={user.timestamp} />
                    </TableCell>
                    <TableCell className="h-12 px-3 py-2">
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

ToolUsersTable.displayName = 'ToolUsersTable';
